/**
 * The page's side of the picture-in-picture's placement, after Codex's anchor bridge: the
 * conversation pane marked `data-pip-anchor-host` is where the stack rests, at one of its four
 * corners, each moved off the elements marked `data-pip-obstacle` (the composer). The Desktop
 * turns these anchors (window content coordinates) into screen positions.
 */

export type PipAlignment = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type PipAnchor = { alignment: PipAlignment; point: { x: number; y: number } };
/** The pane's corners, and the summary panel's picture-in-picture surface while it shows. */
export type PipHostLayout = {
  conversationId: string;
  anchors: PipAnchor[];
  home: PipRect | null;
};
export type PipRect = { x: number; y: number; width: number; height: number };

type Box = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

const PIP_ANCHOR_HOST = 'data-pip-anchor-host';
const PIP_OBSTACLE = 'data-pip-obstacle';
/** The summary panel's surface the picture-in-picture is drawn in (its home). */
const PIP_HOME_SURFACE = 'data-pip-home-surface';

const ALIGNMENTS: readonly PipAlignment[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];
/** Distance from the host's edges. */
const INSET = 24;
/** Room kept around obstacles. */
const OBSTACLE_PADDING = 12;
/** The item's footprint the anchors are computed for (Codex's 250 × 250). */
const FOOTPRINT = { width: 250, height: 250 };
const AVOIDANCE_STEPS = 6;
const OVERLAP_PENALTY = 1e9;
const OVERLAP_AREA_WEIGHT = 1e4;
const PRIORITY_WEIGHT = 1e6;

const box = (left: number, top: number, size: { width: number; height: number }): Box => ({
  left,
  top,
  right: left + size.width,
  bottom: top + size.height,
  width: size.width,
  height: size.height,
});

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

function overlap(a: Box, b: Box): number {
  const width = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const height = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return width <= 0 || height <= 0 ? 0 : width * height;
}

/** Kept inside the host, inset from its edges. */
function contain(rect: Box, host: PipRect): Box {
  return box(
    clamp(rect.left, INSET, Math.max(INSET, host.width - rect.width - INSET)),
    clamp(rect.top, INSET, Math.max(INSET, host.height - rect.height - INSET)),
    rect,
  );
}

function cornerBox(alignment: PipAlignment, host: PipRect): Box {
  const left = alignment.endsWith('left') ? INSET : host.width - FOOTPRINT.width - INSET;
  const top = alignment.startsWith('top') ? INSET : host.height - FOOTPRINT.height - INSET;
  return box(left, top, FOOTPRINT);
}

/** The ways around an obstacle, the most natural for the corner first. */
function detours(alignment: PipAlignment, rect: Box, obstacle: Box) {
  const below = box(rect.left, obstacle.bottom, rect);
  const above = box(rect.left, obstacle.top - rect.height, rect);
  const right = box(obstacle.right, rect.top, rect);
  const left = box(obstacle.left - rect.width, rect.top, rect);
  switch (alignment) {
    case 'top-left':
      return [below, right, left, above];
    case 'top-right':
      return [below, left, right, above];
    case 'bottom-left':
      return [above, right, left, below];
    case 'bottom-right':
      return [above, left, right, below];
  }
}

function cost(rect: Box, priority: number, obstacles: readonly Box[], source: Box): number {
  const overlapping = obstacles.reduce((sum, obstacle) => sum + overlap(rect, obstacle), 0);
  return (
    (overlapping > 0 ? OVERLAP_PENALTY : 0) +
    overlapping * OVERLAP_AREA_WEIGHT +
    priority * PRIORITY_WEIGHT +
    (rect.left - source.left) ** 2 +
    (rect.top - source.top) ** 2
  );
}

function anchorFor(alignment: PipAlignment, host: PipRect, obstacles: readonly Box[]): PipAnchor {
  const source = contain(cornerBox(alignment, host), host);
  let rect = source;
  for (let step = 0; step < AVOIDANCE_STEPS; step += 1) {
    const obstacle = obstacles.find((candidate) => overlap(rect, candidate) > 0);
    if (obstacle === undefined) break;
    let best = rect;
    let bestCost = Number.POSITIVE_INFINITY;
    detours(alignment, rect, obstacle).forEach((detour, priority) => {
      const contained = contain(detour, host);
      const detourCost = cost(contained, priority, obstacles, source);
      if (detourCost < bestCost) {
        best = contained;
        bestCost = detourCost;
      }
    });
    rect = best;
  }
  return {
    alignment,
    point: {
      x: host.x + (alignment.endsWith('left') ? rect.left : rect.right),
      y: host.y + (alignment.startsWith('top') ? rect.top : rect.bottom),
    },
  };
}

/** The host's four anchors (content coordinates) around its obstacles. */
export function pipAnchors(host: PipRect, obstacles: readonly PipRect[]): PipAnchor[] {
  const relative = obstacles.map((obstacle) => {
    const left = obstacle.x - host.x - OBSTACLE_PADDING;
    const top = obstacle.y - host.y - OBSTACLE_PADDING;
    return box(left, top, {
      width: obstacle.width + OBSTACLE_PADDING * 2,
      height: obstacle.height + OBSTACLE_PADDING * 2,
    });
  });
  return ALIGNMENTS.map((alignment) => anchorFor(alignment, host, relative));
}

/**
 * Where an element shows; null when it does not: hidden, or inside an inert part (a closed
 * popover). A pane still fading in is shown already, as in Codex's bridge (no opacity test).
 */
function visibleRect(element: Element): PipRect | null {
  if (element instanceof HTMLElement && element.hidden) return null;
  if (element.closest('[inert]') !== null) return null;
  if (
    typeof element.checkVisibility === 'function'
      ? !element.checkVisibility({ visibilityProperty: true })
      : window.getComputedStyle(element).visibility === 'hidden'
  )
    return null;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
}

/**
 * The current host layout, or null when no conversation pane is on the page. A page another app
 * covers keeps its layout: the Desktop knows whether its window is shown, and the stack stays at
 * its corner as Codex's does.
 */
function readPipHostLayout(document: Document): PipHostLayout | null {
  for (const element of document.querySelectorAll(`[${PIP_ANCHOR_HOST}]`)) {
    const conversationId = element.getAttribute(PIP_ANCHOR_HOST);
    const host = visibleRect(element);
    if (!conversationId || host === null) continue;
    const obstacles = [...document.querySelectorAll(`[${PIP_OBSTACLE}]`)]
      .map(visibleRect)
      .filter((rect): rect is PipRect => rect !== null);
    // The summary may be mounted twice (pinned beside the transcript, in its popover): the one shown.
    let home: PipRect | null = null;
    for (const surface of document.querySelectorAll(`[${PIP_HOME_SURFACE}]`)) {
      home = visibleRect(surface);
      if (home !== null) break;
    }
    return {
      conversationId,
      anchors: pipAnchors(host, obstacles),
      home,
    };
  }
  return null;
}

/**
 * Follows the page's host and obstacles (added, removed, resized, scrolled) and sends their
 * layout whenever it changes. Returns its stop.
 */
export function startPipAnchorBridge(send: (layout: PipHostLayout | null) => void): () => void {
  // A timer, not an animation frame: a covered window paints no frames but still lays out.
  let frame: ReturnType<typeof setTimeout> | null = null;
  let last = '';
  const publish = () => {
    frame = null;
    const layout = readPipHostLayout(document);
    const serialized = JSON.stringify(layout);
    if (serialized === last) return;
    last = serialized;
    send(layout);
  };
  const schedule = () => {
    frame ??= setTimeout(publish, 16);
  };
  const marked = `[${PIP_ANCHOR_HOST}],[${PIP_OBSTACLE}],[${PIP_HOME_SURFACE}]`;
  const resizes = new ResizeObserver(schedule);
  // The marked elements' own visibility; the rest of the page streaming is none of its business.
  const appearance = new MutationObserver(schedule);
  const observeTargets = () => {
    resizes.disconnect();
    appearance.disconnect();
    // Each one and its ancestors: a panel shows or hides by its container (a closed popover).
    const watched = new Set<Element>();
    for (const element of document.querySelectorAll(marked)) {
      resizes.observe(element);
      for (let node: Element | null = element; node !== null && node !== document.body; ) {
        watched.add(node);
        node = node.parentElement;
      }
    }
    for (const node of watched)
      appearance.observe(node, {
        attributeFilter: ['aria-hidden', 'class', 'hidden', 'inert', 'style'],
      });
  };
  const isMarked = (node: Node) =>
    node instanceof Element && (node.matches(marked) || node.querySelector(marked) !== null);
  const mutations = new MutationObserver((records) => {
    if (
      !records.some(
        (record) =>
          record.type === 'attributes' ||
          [...record.addedNodes].some(isMarked) ||
          [...record.removedNodes].some(isMarked),
      )
    )
      return;
    observeTargets();
    schedule();
  });
  mutations.observe(document.body, {
    attributeFilter: [PIP_ANCHOR_HOST, PIP_OBSTACLE, PIP_HOME_SURFACE],
    attributes: true,
    childList: true,
    subtree: true,
  });
  window.addEventListener('resize', schedule);
  // A panel fading in or out (a popover) is only visible once its animation ends.
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);
  document.addEventListener('scroll', schedule, true);
  observeTargets();
  schedule();
  return () => {
    if (frame !== null) clearTimeout(frame);
    resizes.disconnect();
    appearance.disconnect();
    mutations.disconnect();
    window.removeEventListener('resize', schedule);
    document.removeEventListener('transitionend', schedule, true);
    document.removeEventListener('animationend', schedule, true);
    document.removeEventListener('scroll', schedule, true);
    send(null);
  };
}
